import { Loader } from '@/modules/base/classes/loaders'
import { KeyDict } from '@/types'
import { CastProperty } from '@/types/admin'
import { GConstructor, Mixin } from './base'
import { HasQuality } from './hasQuality'
import { HasRegion } from './hasRegion'
import { CastType } from '@/types/casts'

const MACHINE_TYPE_LARGE = 'c5_18xlarge'

const getQualityPresets = (withQuality: HasQuality, castProperties: KeyDict<CastProperty>) => {
  let qualityPresets: string[] = []

  if (withQuality.quality !== null) {
    const properties = castProperties[withQuality.quality]
    if (properties !== undefined) qualityPresets = [...qualityPresets, ...properties.presets]
  }
  if (withQuality.framerate !== null) {
    const properties = castProperties[withQuality.framerate]
    if (properties !== undefined) qualityPresets = [...qualityPresets, ...properties.presets]
  }
  return qualityPresets
}

const getRegionPresets = (withRegion: HasRegion, castProperties: KeyDict<CastProperty>) => {
  return castProperties[withRegion.region]?.presets ?? []
}

const getRegionBackupPresets = (withRegion: HasRegion, castProperties: KeyDict<CastProperty>) => {
  if (!withRegion.regionBackup) return []
  return castProperties[withRegion.regionBackup]?.presets ?? []
}

const getMixerConfigPresets = (size: MixerSize, castType: CastType|undefined) => {
  // TODO: This should get changed to separate mixer_count_<#> and mixer_size_<s|m|l> presets. But needs to be supported
  //       by ngcvp first.
  if (castType === CastType.StationSwitcher) return ['feeds_studio_station']
  if (size === MixerSize.SMALL) return ['feeds_cloudcastv3recordonly']
  else return ['feeds_cloudcastv3']
}

const getMixerAudioTracks = (mixerAudioTrackCount: Number, castType: CastType|undefined) => {
  if (castType === CastType.StationSwitcher) return 'mixer_audio_tracks_obs_4'
  return `mixer_audio_tracks_${mixerAudioTrackCount}`
}

const getMachinePresets = (castProperties: KeyDict<CastProperty>) => {
  return castProperties[MACHINE_TYPE_LARGE]?.presets ?? []
}

export enum MixerSize {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small'
}

export interface HasPresetsProps {
  castType: CastType
  mixerAudioTrackCount: number
  mixerSize: MixerSize
  qualityOverride: string[]
}

// eslint-disable-next-line max-lines-per-function
export function hasPresets<TBase extends GConstructor<HasRegion> & GConstructor<HasQuality>
                          > (Base: TBase, props: Partial<HasPresetsProps> = {}) {
  return class hasPresetsClass extends Base {
    constructor (...arg: any[]) {
      super(...arg)
      this.presets = Loader.loadAndCast(arg[0], 'presets', [])
      this.presetsBackup = Loader.loadAndCast(arg[0], 'presetsBackup', [])
    }

    presets: string[]
    presetsBackup: string[]

    calculatePresets (castProperties: KeyDict<CastProperty>,
        ngcvpVersion: string|null, backup: boolean = false) {
      const machineSize = props.mixerSize ?? MixerSize.MEDIUM
      let newPresets = getMixerConfigPresets(machineSize, props.castType)
      newPresets = [...newPresets, ...getMachinePresets(castProperties)]
      const qualityPresets = props.qualityOverride ? props.qualityOverride : getQualityPresets(this, castProperties)
      newPresets = [...newPresets, ...qualityPresets]
      if (backup) {
        newPresets = [...newPresets, ...getRegionBackupPresets(this, castProperties)]
      } else {
        newPresets = [...newPresets, ...getRegionPresets(this, castProperties)]
      }
      if (ngcvpVersion !== null) newPresets = [...newPresets, ngcvpVersion]
      const mixerAudioTracks = getMixerAudioTracks(props.mixerAudioTrackCount ?? 16, props.castType)
      newPresets = [...newPresets, mixerAudioTracks]
      if (backup) {
        this.presetsBackup = newPresets
      } else {
        this.presets = newPresets
      }
      return this
    }
  }
}


export type HasPresets = Mixin<typeof hasPresets>

